import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SEO, { buildBreadcrumbSchema } from '@/components/SEO';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const termsSchema = [
  buildBreadcrumbSchema([
    { name: 'Home', href: '/' },
    { name: 'Terms of Use', href: '/terms' },
  ]),
  {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Terms of Use | Fore Capital',
  url: `${SITE_URL}/terms`,
  description: 'Fore Capital Terms of Use. Read our terms and conditions for using the Fore Capital website and payment infrastructure services.',
  },
];

export default function TermsOfUse() {
  return (
    <div className="min-h-screen bg-[#F9F9F7]" style={{ fontFamily: "'Google Sans Flex', sans-serif" }}>
      <SEO
        title="Terms of Use | Fore Capital"
        description="Fore Capital Terms of Use. Read our terms and conditions for using the Fore Capital website and payment infrastructure services."
        keywords="terms of use, terms and conditions, service agreement, Fore Capital terms"
        pathname="/terms"
        schema={termsSchema}
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="bg-[#122852] py-20 px-5 md:px-[75px]">
          <div className="max-w-[1400px] mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 mb-6" aria-label="Breadcrumb">
              <a
                href="/"
                className="text-white/40 text-[11px] uppercase tracking-[0.18em] hover:text-white/70 transition-colors duration-200 cursor-pointer"
                style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
              >
                Home
              </a>
              <span className="text-white/25 text-[11px]">/</span>
              <span
                className="text-[#d3b366] text-[11px] uppercase tracking-[0.18em]"
                style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
              >
                Terms of Use
              </span>
            </nav>
            <p
              className="text-[#d3b366] text-[10px] uppercase tracking-[0.25em] font-medium mb-4"
              style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
            >
              Legal
            </p>
            <h1
              className="text-white text-3xl md:text-4xl lg:text-[48px] font-light leading-[1.1]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Terms of Use
            </h1>
          </div>
        </section>

        {/* Content */}
        <section className="py-20 px-5 md:px-[75px]">
          <div className="max-w-[900px] mx-auto">
            <div className="space-y-12">
              <div>
                <h2
                  className="text-[#061e45] text-xl font-medium mb-4"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                >
                  1. Agreement to Terms
                </h2>
                <p
                  className="text-[#1A1A1A]/60 text-[15px] leading-relaxed"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
                >
                  By accessing or using the Fore Capital website and services, you agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use our website or services.
                </p>
              </div>

              <div>
                <h2
                  className="text-[#061e45] text-xl font-medium mb-4"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                >
                  2. Use of Services
                </h2>
                <p
                  className="text-[#1A1A1A]/60 text-[15px] leading-relaxed mb-3"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
                >
                  You agree to use our services only for lawful purposes and in accordance with these Terms. You agree not to:
                </p>
                <ul className="space-y-2 text-[#1A1A1A]/60 text-[15px] leading-relaxed" style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}>
                  <li className="flex items-start gap-2">
                    <span className="text-[#a8894a] mt-1">&#8226;</span>
                    <span>Use our services in any way that violates applicable laws or regulations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#a8894a] mt-1">&#8226;</span>
                    <span>Attempt to gain unauthorized access to our systems or networks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#a8894a] mt-1">&#8226;</span>
                    <span>Interfere with or disrupt the integrity or performance of our services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#a8894a] mt-1">&#8226;</span>
                    <span>Transmit any viruses, malware, or other harmful code</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#a8894a] mt-1">&#8226;</span>
                    <span>Engage in any activity that could damage, disable, or impair our services</span>
                  </li>
                </ul>
              </div>

              <div>
                <h2
                  className="text-[#061e45] text-xl font-medium mb-4"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                >
                  3. Intellectual Property
                </h2>
                <p
                  className="text-[#1A1A1A]/60 text-[15px] leading-relaxed"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
                >
                  All content on this website, including text, graphics, logos, images, and software, is the property of Fore Capital or its licensors and is protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, modify, or create derivative works without our prior written consent.
                </p>
              </div>

              <div>
                <h2
                  className="text-[#061e45] text-xl font-medium mb-4"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                >
                  4. Account Registration
                </h2>
                <p
                  className="text-[#1A1A1A]/60 text-[15px] leading-relaxed"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
                >
                  To access certain features of our services, you may need to create an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
                </p>
              </div>

              <div>
                <h2
                  className="text-[#061e45] text-xl font-medium mb-4"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                >
                  5. Payment Terms
                </h2>
                <p
                  className="text-[#1A1A1A]/60 text-[15px] leading-relaxed"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
                >
                  Fees for our services are outlined in your service agreement. All fees are non-refundable unless otherwise specified. You are responsible for all applicable taxes. We reserve the right to modify our pricing with 30 days advance notice.
                </p>
              </div>

              <div>
                <h2
                  className="text-[#061e45] text-xl font-medium mb-4"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                >
                  6. Limitation of Liability
                </h2>
                <p
                  className="text-[#1A1A1A]/60 text-[15px] leading-relaxed"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
                >
                  To the maximum extent permitted by law, Fore Capital shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of our services. Our total liability shall not exceed the amount you paid for our services in the 12 months preceding the claim.
                </p>
              </div>

              <div>
                <h2
                  className="text-[#061e45] text-xl font-medium mb-4"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                >
                  7. Indemnification
                </h2>
                <p
                  className="text-[#1A1A1A]/60 text-[15px] leading-relaxed"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
                >
                  You agree to indemnify and hold harmless Fore Capital and its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising out of your use of our services or violation of these Terms.
                </p>
              </div>

              <div>
                <h2
                  className="text-[#061e45] text-xl font-medium mb-4"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                >
                  8. Termination
                </h2>
                <p
                  className="text-[#1A1A1A]/60 text-[15px] leading-relaxed"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
                >
                  We may terminate or suspend your access to our services at any time, with or without cause, with or without notice. Upon termination, your right to use our services will immediately cease.
                </p>
              </div>

              <div>
                <h2
                  className="text-[#061e45] text-xl font-medium mb-4"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                >
                  9. Governing Law
                </h2>
                <p
                  className="text-[#1A1A1A]/60 text-[15px] leading-relaxed"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
                >
                  These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, without regard to its conflict of law provisions. Any disputes shall be resolved in the courts of Delaware.
                </p>
              </div>

              <div>
                <h2
                  className="text-[#061e45] text-xl font-medium mb-4"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                >
                  10. Changes to Terms
                </h2>
                <p
                  className="text-[#1A1A1A]/60 text-[15px] leading-relaxed"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
                >
                  We reserve the right to modify these Terms at any time. The updated version will be posted on this page with an updated effective date. Your continued use of our services after any changes constitutes acceptance of the new Terms.
                </p>
              </div>

              <div>
                <h2
                  className="text-[#061e45] text-xl font-medium mb-4"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                >
                  11. Contact Information
                </h2>
                <p
                  className="text-[#1A1A1A]/60 text-[15px] leading-relaxed"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400 }}
                >
                  If you have any questions about these Terms, please contact us at legal@forecapital.com or through our <a href="/contact" className="text-[#a8894a] hover:underline cursor-pointer">Contact page</a>.
                </p>
              </div>

              <div className="pt-8 border-t border-[#1A1A1A]/10">
                <p
                  className="text-[#1A1A1A]/40 text-xs"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif" }}
                >
                  Last Modified: April 24, 2026
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}